import { documentiFixtures } from "./fixtures/documenti";

export type DocumentoTipo = "PDF" | "P7M" | "DOCX" | "XLSX" | "JPG";
export type DocumentoStato = "Verificato" | "Da verificare" | "In caricamento";
export type DocumentoCollegamentoTipo = "Bando" | "Progetto" | "Ente";

export type Documento = {
  id: string;
  nome: string;
  tipo: DocumentoTipo;
  dimensione: number;
  aggiornatoIl: string;
  stato: DocumentoStato;
  descrizione: string;
  collegatoA: string;
  collegamentoTipo?: DocumentoCollegamentoTipo;
};

export type DocumentoInput = Pick<Documento, "nome" | "tipo" | "collegatoA" | "descrizione"> & {
  collegamentoTipo: DocumentoCollegamentoTipo;
};

export type DocumentiFilters = { query?: string; tipo?: DocumentoTipo };

let documents = [...documentiFixtures];

const delay = (milliseconds: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));

export async function listDocumenti(filters: DocumentiFilters = {}): Promise<Documento[]> {
  await delay(300);
  const query = filters.query?.trim().toLocaleLowerCase("it-IT") ?? "";
  return documents.filter((document) => {
    const searchableText = `${document.nome} ${document.descrizione} ${document.collegatoA}`.toLocaleLowerCase("it-IT");
    return (!query || searchableText.includes(query)) && (!filters.tipo || document.tipo === filters.tipo);
  });
}

export async function getDocumento(id: string): Promise<Documento | undefined> {
  await delay(180);
  return documents.find((document) => document.id === id);
}

export async function uploadDocumento(file: File, input?: Partial<DocumentoInput>): Promise<Documento> {
  await delay(500);
  if (file.name.toLocaleLowerCase("it-IT").includes("errore")) {
    throw new Error("Simulated document add failure");
  }
  const extension = file.name.split(".").pop()?.toUpperCase();
  const tipo: DocumentoTipo = extension === "P7M" ? "P7M" : extension === "DOCX" || extension === "XLSX" || extension === "JPG" || extension === "JPEG" ? (extension === "JPEG" ? "JPG" : extension) : "PDF";
  const document: Documento = {
    id: `documento-${Date.now()}`,
    nome: input?.nome?.trim() || file.name,
    tipo: input?.tipo || tipo,
    dimensione: file.size,
    aggiornatoIl: new Date().toISOString(),
    stato: "Da verificare",
    descrizione: input?.descrizione?.trim() || "Documento appena aggiunto al raccoglitore del Comune.",
    collegatoA: input?.collegatoA || "Non ancora collegato",
    collegamentoTipo: input?.collegamentoTipo,
  };
  documents = [document, ...documents];
  return document;
}

export async function replaceDocumento(id: string, file: File): Promise<Documento | undefined> {
  await delay(500);
  const index = documents.findIndex((document) => document.id === id);
  if (index < 0) return undefined;
  const extension = file.name.split(".").pop()?.toUpperCase();
  const tipo: DocumentoTipo = extension === "P7M" ? "P7M" : extension === "DOCX" || extension === "XLSX" || extension === "JPG" || extension === "JPEG" ? (extension === "JPEG" ? "JPG" : extension) : "PDF";
  documents[index] = { ...documents[index], nome: file.name, tipo, dimensione: file.size, aggiornatoIl: new Date().toISOString(), stato: "Da verificare" };
  return documents[index];
}

export async function deleteDocumento(id: string): Promise<void> {
  await delay(350);
  documents = documents.filter((document) => document.id !== id);
}

export async function updateDocumento(id: string, changes: Pick<Documento, "collegatoA" | "descrizione"> & Pick<Documento, "nome" | "tipo" | "collegamentoTipo">): Promise<Documento | undefined> {
  await delay(300);
  const index = documents.findIndex((document) => document.id === id);
  if (index < 0) return undefined;
  documents[index] = { ...documents[index], ...changes, aggiornatoIl: new Date().toISOString() };
  return documents[index];
}

export async function downloadDocumento(id: string): Promise<Documento | undefined> {
  await delay(260);
  return documents.find((document) => document.id === id);
}
